import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import {
  EditAmountDialogCloseResult,
  EditAmountService,
  formatEditAmountErrorText,
  getEditAmountWarningToast,
  isEditAmountBlockedByOrder
} from 'src/entities/file';
import { NotificationService } from 'src/app/shared/notification.service';
import {
  AMOUNT_DECIMALS_ERROR,
  formatMoneyDisplay,
  restrictAmountText
} from 'src/shared/lib/money';
import Swal from 'sweetalert2';
import { DialogEditTicketAmountData } from './edit-ticket-amount-dialog.config';

@Component({
  selector: 'app-dialog-edit-ticket-amount',
  templateUrl: './dialog-edit-ticket-amount.component.html',
  styleUrls: ['./dialog-edit-ticket-amount.component.css']
})
export class DialogEditTicketAmountComponent implements OnInit, OnDestroy {
  loading = false;
  saveError: string | null = null;
  amountError: string | null = null;
  form: FormGroup;

  readonly isSoles: boolean;
  readonly currencyLabel: string;
  readonly currencyPrefix: string;
  readonly tipoCambio: number | null;
  readonly serviceName: string | null;
  readonly currentTotalLabel: string | null;

  private saveSub?: Subscription;
  private usuario = '';
  private email = '';

  constructor(
    public dialogRef: MatDialogRef<
      DialogEditTicketAmountComponent,
      EditAmountDialogCloseResult
    >,
    private readonly fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: DialogEditTicketAmountData,
    private readonly editAmountService: EditAmountService,
    private readonly notificationService: NotificationService
  ) {
    this.form = this.fb.group({
      monto: new FormControl(null, [Validators.required, Validators.min(0.01)])
    });

    this.isSoles = this.resolveIsSoles(data);
    this.currencyPrefix = this.isSoles ? 'S/' : '$';
    this.currencyLabel = this.isSoles ? 'Soles' : 'USD';
    this.tipoCambio = this.resolveTipoCambio(data);
    this.serviceName = data?.nombreServicio?.trim() || null;

    const current = Number(data?.currentTotal);
    this.currentTotalLabel =
      Number.isFinite(current) && current > 0
        ? `${this.currencyPrefix} ${formatMoneyDisplay(current)}`
        : null;
  }

  ngOnInit(): void {
    const nombre = localStorage.getItem('nombre') ?? '';
    const apellido = localStorage.getItem('apellido') ?? '';
    this.usuario = `${nombre} ${apellido}`.trim();
    this.email = localStorage.getItem('email') ?? '';

    const current =
      this.data?.currentTotal != null
        ? Number(this.data.currentTotal)
        : null;
    if (current != null && Number.isFinite(current) && current > 0) {
      this.form.patchValue({ monto: current });
    }
  }

  ngOnDestroy(): void {
    this.saveSub?.unsubscribe();
  }

  get siblingTicketsSum(): number {
    return this.sumOtherTickets();
  }

  get projectedServiceTotal(): number {
    const value = Number(this.form.value.monto);
    if (!Number.isFinite(value) || value <= 0) {
      return this.siblingTicketsSum;
    }
    return this.siblingTicketsSum + value;
  }

  get canSubmit(): boolean {
    return (
      !this.loading &&
      this.form.valid &&
      Boolean(this.data?.ticketId) &&
      Boolean(this.data?.serviceId) &&
      Boolean(this.email)
    );
  }

  onNumberInput(event: Event): void {
    this.saveError = null;
    this.amountError = null;
    const input = event.target as HTMLInputElement;
    const restricted = restrictAmountText(input.value);
    if (restricted.exceeded) {
      this.amountError = AMOUNT_DECIMALS_ERROR;
    }
    if (input.value !== restricted.text) {
      this.form.get('monto')?.setValue(restricted.text);
    }
  }

  onSave(): void {
    if (this.loading) {
      return;
    }

    if (!this.email) {
      this.saveError = 'Sesión sin email. Vuelve a iniciar sesión.';
      return;
    }

    const amount = Number(this.form.value.monto);
    if (!Number.isFinite(amount) || amount <= 0) {
      this.amountError = 'Ingresa un monto mayor a 0';
      return;
    }
    if (restrictAmountText(String(this.form.value.monto)).exceeded) {
      this.amountError = AMOUNT_DECIMALS_ERROR;
      return;
    }

    if (this.tipoCambio != null && this.tipoCambio <= 0) {
      this.saveError =
        'Falta tipo de cambio válido. No se puede convertir el monto.';
      return;
    }

    this.loading = true;
    this.saveError = null;
    this.dialogRef.disableClose = true;

    this.saveSub?.unsubscribe();
    this.saveSub = this.editAmountService
      .editTicketAmount({
        ticketId: String(this.data.ticketId),
        serviceId: String(this.data.serviceId),
        amount,
        createdBy: { name: this.usuario || this.email, email: this.email }
      })
      .subscribe({
        next: (result) => {
          this.loading = false;
          this.dialogRef.disableClose = false;

          if (!result?.ok) {
            this.saveError = 'No se pudo actualizar el monto. Reintenta.';
            return;
          }

          this.showWarnings(result.warnings);
          this.dialogRef.close({ success: true, result });
        },
        error: (err) => {
          this.loading = false;
          this.dialogRef.disableClose = false;
          this.saveError = formatEditAmountErrorText(err);

          if (isEditAmountBlockedByOrder(err)) {
            this.notificationService.showErrorssAlertPersonalizado(
              'No se puede editar',
              this.saveError,
              () =>
                this.dialogRef.close({ success: false, blockedByOrder: true })
            );
          }
        }
      });
  }

  onCancel(): void {
    if (this.loading) {
      return;
    }
    this.dialogRef.close({ success: false });
  }

  private showWarnings(
    warnings: { code?: string | null; message?: string | null }[] | null | undefined
  ): void {
    if (!warnings?.length) {
      this.notificationService.showSuccessAlertPersonalizado(
        'Monto actualizado',
        'El ticket y el total del servicio se actualizaron.',
        () => undefined
      );
      return;
    }

    let softSuccess = false;
    for (const warning of warnings) {
      const toast = getEditAmountWarningToast(warning);
      if (!toast) continue;
      if (warning.code === 'IDEMPOTENT_NO_CHANGE') {
        softSuccess = true;
      }
      Swal.fire({
        position: 'top-end',
        icon: toast.icon,
        title: toast.title,
        text: toast.text,
        showConfirmButton: false,
        timer: 4000
      });
    }

    if (!softSuccess) {
      this.notificationService.showSuccessAlertPersonalizado(
        'Monto actualizado',
        'El ticket y el total del servicio se actualizaron.',
        () => undefined
      );
    }
  }

  private resolveIsSoles(data: DialogEditTicketAmountData): boolean {
    const candidates = [
      data?.tipoMoneda,
      data?.moneda,
      data?.objTarifa?.monedaTarifa
    ];

    for (const raw of candidates) {
      if (raw == null || raw === '') continue;
      const value = String(raw).toUpperCase().trim();
      if (
        value === 'D' ||
        value === '$' ||
        value === 'DOLAR' ||
        value === 'DOLARES' ||
        value === 'USD'
      ) {
        return false;
      }
      if (
        value === 'S' ||
        value === 'S/' ||
        value === 'SOLES' ||
        value === 'PEN' ||
        value === 'SOL'
      ) {
        return true;
      }
    }

    return true;
  }

  private resolveTipoCambio(data: DialogEditTicketAmountData): number | null {
    const candidates = [
      data?.tipoCambio,
      data?.objTarifa?.tipoCambio,
      data?.servicioTipoCambio
    ];

    for (const raw of candidates) {
      const value = Number(raw);
      if (Number.isFinite(value) && value > 0) {
        return value;
      }
    }

    return null;
  }

  private sumOtherTickets(): number {
    const tickets = Array.isArray(this.data?.tickets) ? this.data.tickets : [];
    return tickets.reduce((acc: number, item: any) => {
      if (String(item?.id) === String(this.data.ticketId)) {
        return acc;
      }
      const value = Number(item?.monto ?? item?.total ?? 0);
      return acc + (Number.isFinite(value) ? value : 0);
    }, 0);
  }

}
